import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { buildPaymentAgreementText } from '@shared/payments/payment-agreement';
import type {
  CompanyProfileRecord,
  CustomerSummaryRecord,
  DocumentPartySnapshotRecord,
  PaymentAgreementInput,
  PaymentCollectionRecord,
  PaymentCurrency,
  PaymentOrderStatus
} from '@shared/types';
import { subscribeToShellDeleteRequests } from '@renderer/app/events/shell-delete-events';
import { EmptyCollectionState } from '@renderer/shared/components/collection-scaffold';
import { Icon } from '@renderer/shared/components/icons';
import { PanelCard, SectionHeader, SelectField, StatusBadge, TextAreaField, TextField } from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { createPaymentOrder, deletePaymentOrders, listPaymentOrders, syncPaymentOrder } from './payments-repository';
import { selectPaymentRows, selectPaymentsSummary } from './payments-selectors';
import { usePaymentsViewStore } from './payments-view-store';

type SortDirection = 'asc' | 'desc';

interface CreatePaymentDraft {
  customerId: string;
  projectId: string;
  contractId: string;
  invoiceId: string;
  issuerCompanyProfileId: string;
  title: string;
  serviceSummary: string;
  amount: string;
  currency: PaymentCurrency;
  expiresAt: string;
  agreementDate: string;
  issuerRepresentativeName: string;
  customerLegalName: string;
  customerIdentifier: string;
  customerAddress: string;
  customerRepresentativeName: string;
  projectDescription: string;
  paymentTermsText: string;
  deliveryWorkingDays: string;
  revisionRounds: string;
  acceptanceWorkingDays: string;
  warrantyDays: string;
}

const paymentStatuses: PaymentOrderStatus[] = [
  'draft',
  'ready',
  'sent',
  'viewed',
  'accepted',
  'checkout_started',
  'paid',
  'payment_failed',
  'cancelled',
  'expired',
  'archived'
];

function toIsoDate(value: string): string | null {
  return value ? `${value}T12:00:00.000Z` : null;
}

function todayInputValue(): string {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
}

function toPositiveInteger(value: string): number | null {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function toNonNegativeInteger(value: string): number | null {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

function makeInlineAddress(value: string): DocumentPartySnapshotRecord['billingAddress'] {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  return {
    line1: trimmed,
    city: null,
    county: null,
    postalCode: null,
    country: 'Romania',
    formatted: trimmed
  };
}

function buildAgreementInputFromDraft(draft: CreatePaymentDraft): PaymentAgreementInput {
  return {
    agreementDate: toIsoDate(draft.agreementDate),
    issuerRepresentativeName: draft.issuerRepresentativeName.trim() || null,
    customerLegalName: draft.customerLegalName.trim() || null,
    customerIdentifier: draft.customerIdentifier.trim() || null,
    customerAddress: draft.customerAddress.trim() || null,
    customerRepresentativeName: draft.customerRepresentativeName.trim() || null,
    projectDescription: draft.projectDescription.trim() || draft.serviceSummary.trim() || draft.title.trim() || null,
    paymentTermsText: draft.paymentTermsText.trim() || null,
    deliveryWorkingDays: toPositiveInteger(draft.deliveryWorkingDays),
    revisionRounds: toNonNegativeInteger(draft.revisionRounds),
    acceptanceWorkingDays: toPositiveInteger(draft.acceptanceWorkingDays),
    warrantyDays: toPositiveInteger(draft.warrantyDays)
  };
}

function buildCustomerPreviewSnapshot(customer: CustomerSummaryRecord | undefined, draft: CreatePaymentDraft): DocumentPartySnapshotRecord {
  const legalName = draft.customerLegalName.trim() || customer?.name || 'Client';
  const address = makeInlineAddress(draft.customerAddress);

  return {
    displayName: legalName,
    legalName,
    entityType: customer?.type ?? 'company',
    cui: draft.customerIdentifier.trim() || null,
    registrationNumber: null,
    vatPayer: null,
    billingAddress: address,
    signingAddress: address,
    representative: {
      name: draft.customerRepresentativeName.trim() || legalName,
      role: null
    },
    email: null,
    phone: null,
    bankAccount: null
  };
}

function buildIssuerPreviewSnapshot(profile: CompanyProfileRecord | undefined, draft: CreatePaymentDraft): DocumentPartySnapshotRecord {
  const legalName = profile?.legalName ?? profile?.name ?? 'Alttvision Studio SRL';
  const address = profile
    ? {
        line1: profile.addressLine,
        city: profile.city,
        county: profile.county,
        postalCode: profile.postalCode,
        country: profile.country,
        formatted: [profile.addressLine, profile.city, profile.county, profile.postalCode, profile.country].filter(Boolean).join(', ')
      }
    : null;

  return {
    displayName: profile?.name ?? legalName,
    legalName,
    entityType: profile?.entityType ?? 'company',
    cui: profile?.cui ?? null,
    registrationNumber: profile?.registrationNumber ?? null,
    vatPayer: profile?.vatPayer ?? null,
    billingAddress: address,
    signingAddress: address,
    representative: {
      name: draft.issuerRepresentativeName.trim() || legalName,
      role: 'Emitent'
    },
    email: 'cosmin@alttvision.com',
    phone: profile?.phone ?? null,
    bankAccount: profile
      ? {
          bankName: profile.bankName,
          iban: profile.iban,
          swift: profile.swift
        }
      : null
  };
}

function createDraft(collection: PaymentCollectionRecord | null): CreatePaymentDraft {
  const customer = collection?.customerSummaries[0];
  const issuer = collection?.companyProfiles.find((profile) => profile.isDefault) ?? collection?.companyProfiles[0];

  return {
    customerId: customer?.id ?? '',
    projectId: '',
    contractId: '',
    invoiceId: '',
    issuerCompanyProfileId: issuer?.id ?? '',
    title: '',
    serviceSummary: '',
    amount: '',
    currency: 'RON',
    expiresAt: '',
    agreementDate: todayInputValue(),
    issuerRepresentativeName: issuer?.legalName ?? issuer?.name ?? '',
    customerLegalName: customer?.name ?? '',
    customerIdentifier: '',
    customerAddress: '',
    customerRepresentativeName: customer?.primaryContactName ?? customer?.name ?? '',
    projectDescription: '',
    paymentTermsText: '100% avans',
    deliveryWorkingDays: '10',
    revisionRounds: '2',
    acceptanceWorkingDays: '5',
    warrantyDays: '14'
  };
}

export function PaymentsScreen(): JSX.Element {
  const navigate = useNavigate();
  const { locale, t } = useI18n();
  const [collection, setCollection] = useState<PaymentCollectionRecord | null>(null);
  const [draft, setDraft] = useState<CreatePaymentDraft>(() => createDraft(null));
  const [error, setError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const { statusFilter, customerFilter, currencyFilter, sort, setStatusFilter, setCustomerFilter, setCurrencyFilter, setSort } =
    usePaymentsViewStore();
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  useEffect(() => {
    let active = true;

    setError(null);
    void listPaymentOrders()
      .then((nextCollection) => {
        if (!active) {
          return;
        }
        setCollection(nextCollection);
        setDraft((current) => (current.customerId ? current : createDraft(nextCollection)));
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Payments unavailable.');
        }
      });

    return () => {
      active = false;
    };
  }, [reloadToken]);

  useEffect(
    () =>
      subscribeToShellDeleteRequests((detail) => {
        if (detail.domain !== 'payments' || detail.ids.length === 0) {
          return;
        }

        void deletePaymentOrders({ ids: detail.ids }).then((nextCollection) => {
          setCollection(nextCollection);
        });
      }),
    []
  );

  const rows = useMemo(
    () =>
      collection
        ? selectPaymentRows(collection, {
            locale,
            statusFilter,
            customerFilter,
            currencyFilter,
            sort,
            sortDirection,
            t
          })
        : [],
    [collection, currencyFilter, customerFilter, locale, sort, sortDirection, statusFilter, t]
  );
  const summary = useMemo(() => (collection ? selectPaymentsSummary(collection) : null), [collection]);
  const customerOptions = [
    { value: 'all', label: t('payments.filters.allCustomers') },
    ...(collection?.customerSummaries.map((customer) => ({ value: customer.id, label: customer.name })) ?? [])
  ];
  const draftCustomerProjects = collection?.projectSummaries.filter((project) => project.customerId === draft.customerId) ?? [];
  const draftCustomerContracts = collection?.contractSummaries.filter((contract) => contract.customerId === draft.customerId) ?? [];
  const draftCustomerInvoices = collection?.invoiceSummaries.filter((invoice) => invoice.customerId === draft.customerId) ?? [];
  const selectedCustomer = collection?.customerSummaries.find((customer) => customer.id === draft.customerId);
  const selectedIssuer = collection?.companyProfiles.find((profile) => profile.id === draft.issuerCompanyProfileId);
  const agreementNumbersValid =
    Boolean(toPositiveInteger(draft.deliveryWorkingDays)) &&
    toNonNegativeInteger(draft.revisionRounds) !== null &&
    Boolean(toPositiveInteger(draft.acceptanceWorkingDays)) &&
    Boolean(toPositiveInteger(draft.warrantyDays));
  const agreementPreviewText = useMemo(() => {
    const amount = Number(draft.amount);
    return buildPaymentAgreementText({
      title: draft.title.trim() || t('payments.create.previewUntitled'),
      serviceSummary: draft.serviceSummary.trim() || draft.projectDescription.trim() || t('payments.create.previewService'),
      amountMinor: Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : 0,
      currency: draft.currency,
      issuer: buildIssuerPreviewSnapshot(selectedIssuer, draft),
      customer: buildCustomerPreviewSnapshot(selectedCustomer, draft),
      agreement: buildAgreementInputFromDraft(draft)
    });
  }, [draft, selectedCustomer, selectedIssuer, t]);
  const createDisabled =
    isSaving ||
    !draft.customerId ||
    !draft.issuerCompanyProfileId ||
    !draft.title.trim() ||
    Number(draft.amount) <= 0 ||
    !draft.paymentTermsText.trim() ||
    !draft.agreementDate ||
    !agreementNumbersValid;

  async function handleCreatePayment(): Promise<void> {
    setIsSaving(true);
    setMutationError(null);

    try {
      const amount = Number(draft.amount);
      if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error(t('payments.errors.invalidAmount'));
      }

      const detail = await createPaymentOrder({
        customerId: draft.customerId,
        title: draft.title.trim(),
        amountMajor: amount,
        currency: draft.currency,
        issuerCompanyProfileId: draft.issuerCompanyProfileId,
        projectId: draft.projectId || null,
        contractId: draft.contractId || null,
        invoiceId: draft.invoiceId || null,
        serviceSummary: draft.serviceSummary.trim() || null,
        expiresAt: toIsoDate(draft.expiresAt),
        agreement: buildAgreementInputFromDraft(draft)
      });
      navigate(`/payments/${detail.paymentOrder.id}`);
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Payment creation failed.');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSync(paymentOrderId: string): Promise<void> {
    setMutationError(null);
    try {
      await syncPaymentOrder(paymentOrderId);
      setReloadToken((value) => value + 1);
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Payment sync failed.');
    }
  }

  if (error) {
    return (
      <div className="av-page">
        <EmptyCollectionState title={t('payments.title')} description={error} />
      </div>
    );
  }

  if (!collection || !summary) {
    return <div className="av-page" />;
  }

  return (
    <div className="av-page">
      <SectionHeader
        title={t('payments.title')}
        subtitle={t('payments.subtitle', {
          active: summary.activeCount,
          archived: summary.archivedCount
        })}
        actions={
          <Link className="av-primary-button" to="/settings/payments">
            <Icon name="settings" />
            <span>{t('payments.actions.configure')}</span>
          </Link>
        }
      />

      <PanelCard title={t('payments.create.title')}>
        <div className="av-payment-create-flow">
          <section className="av-payment-create-step">
            <header className="av-payment-create-step__header">
              <h3>{t('payments.create.sections.clientIssuer')}</h3>
              <p>{t('payments.create.snapshotNote')}</p>
            </header>
            <div className="av-payment-create-grid">
              <label className="av-field">
                <span>{t('payments.fields.customer')}</span>
                <SelectField
                  options={collection.customerSummaries.map((customer) => ({ value: customer.id, label: customer.name }))}
                  value={draft.customerId}
                  onChange={(event) => {
                    const nextCustomerId = event.target.value;
                    const nextCustomer = collection.customerSummaries.find((customer) => customer.id === nextCustomerId);
                    const previousCustomerName = selectedCustomer?.name ?? '';

                    setDraft((current) => ({
                      ...current,
                      customerId: nextCustomerId,
                      projectId: '',
                      contractId: '',
                      invoiceId: '',
                      customerLegalName:
                        !current.customerLegalName.trim() || current.customerLegalName === previousCustomerName
                          ? nextCustomer?.name ?? ''
                          : current.customerLegalName,
                      customerRepresentativeName:
                        !current.customerRepresentativeName.trim() || current.customerRepresentativeName === previousCustomerName
                          ? nextCustomer?.primaryContactName ?? nextCustomer?.name ?? ''
                          : current.customerRepresentativeName
                    }));
                  }}
                />
              </label>
              <label className="av-field">
                <span>{t('payments.fields.issuer')}</span>
                <SelectField
                  options={collection.companyProfiles.map((profile) => ({ value: profile.id, label: profile.name }))}
                  value={draft.issuerCompanyProfileId}
                  onChange={(event) => {
                    const nextIssuerId = event.target.value;
                    const nextIssuer = collection.companyProfiles.find((profile) => profile.id === nextIssuerId);
                    const previousIssuerRepresentative = selectedIssuer?.legalName ?? selectedIssuer?.name ?? '';

                    setDraft((current) => ({
                      ...current,
                      issuerCompanyProfileId: nextIssuerId,
                      issuerRepresentativeName:
                        !current.issuerRepresentativeName.trim() || current.issuerRepresentativeName === previousIssuerRepresentative
                          ? nextIssuer?.legalName ?? nextIssuer?.name ?? ''
                          : current.issuerRepresentativeName
                    }));
                  }}
                />
              </label>
              <label className="av-field">
                <span>{t('payments.fields.agreementDate')}</span>
                <TextField
                  type="date"
                  value={draft.agreementDate}
                  onValueChange={(agreementDate) => setDraft((current) => ({ ...current, agreementDate }))}
                />
              </label>
              <label className="av-field">
                <span>{t('payments.fields.issuerRepresentative')}</span>
                <TextField
                  value={draft.issuerRepresentativeName}
                  onValueChange={(issuerRepresentativeName) => setDraft((current) => ({ ...current, issuerRepresentativeName }))}
                />
              </label>
              <label className="av-field">
                <span>{t('payments.fields.customerLegalName')}</span>
                <TextField
                  value={draft.customerLegalName}
                  onValueChange={(customerLegalName) => setDraft((current) => ({ ...current, customerLegalName }))}
                />
              </label>
              <label className="av-field">
                <span>{t('payments.fields.customerIdentifier')}</span>
                <TextField
                  value={draft.customerIdentifier}
                  onValueChange={(customerIdentifier) => setDraft((current) => ({ ...current, customerIdentifier }))}
                />
              </label>
              <label className="av-field">
                <span>{t('payments.fields.customerRepresentative')}</span>
                <TextField
                  value={draft.customerRepresentativeName}
                  onValueChange={(customerRepresentativeName) => setDraft((current) => ({ ...current, customerRepresentativeName }))}
                />
              </label>
              <label className="av-field av-field--span-2">
                <span>{t('payments.fields.customerAddress')}</span>
                <TextAreaField
                  rows={2}
                  value={draft.customerAddress}
                  onValueChange={(customerAddress) => setDraft((current) => ({ ...current, customerAddress }))}
                />
              </label>
            </div>
          </section>

          <section className="av-payment-create-step">
            <header className="av-payment-create-step__header">
              <h3>{t('payments.create.sections.projectTerms')}</h3>
              <p>{t('payments.create.termsNote')}</p>
            </header>
            <div className="av-payment-create-grid">
              <label className="av-field av-field--span-2">
                <span>{t('payments.fields.title')}</span>
                <TextField value={draft.title} onValueChange={(title) => setDraft((current) => ({ ...current, title }))} />
              </label>
              <label className="av-field">
                <span>{t('payments.fields.amount')}</span>
                <TextField value={draft.amount} inputMode="decimal" onValueChange={(amount) => setDraft((current) => ({ ...current, amount }))} />
              </label>
              <label className="av-field">
                <span>{t('payments.fields.currency')}</span>
                <SelectField
                  options={(['RON', 'EUR', 'USD'] satisfies PaymentCurrency[]).map((currency) => ({ value: currency, label: currency }))}
                  value={draft.currency}
                  onChange={(event) => setDraft((current) => ({ ...current, currency: event.target.value as PaymentCurrency }))}
                />
              </label>
              <label className="av-field">
                <span>{t('payments.fields.project')}</span>
                <SelectField
                  options={[{ value: '', label: t('common.notSet') }, ...draftCustomerProjects.map((project) => ({ value: project.id, label: project.name }))]}
                  value={draft.projectId}
                  onChange={(event) => setDraft((current) => ({ ...current, projectId: event.target.value }))}
                />
              </label>
              <label className="av-field">
                <span>{t('payments.fields.expiresAt')}</span>
                <TextField type="date" value={draft.expiresAt} onValueChange={(expiresAt) => setDraft((current) => ({ ...current, expiresAt }))} />
              </label>
              <label className="av-field">
                <span>{t('payments.fields.contract')}</span>
                <SelectField
                  options={[
                    { value: '', label: t('common.notSet') },
                    ...draftCustomerContracts.map((contract) => ({ value: contract.id, label: `${contract.contractNumber} - ${contract.title}` }))
                  ]}
                  value={draft.contractId}
                  onChange={(event) => setDraft((current) => ({ ...current, contractId: event.target.value }))}
                />
                {draft.customerId && draftCustomerContracts.length === 0 ? (
                  <em className="av-field-note">{t('payments.empty.noContractsForCustomer')}</em>
                ) : null}
              </label>
              <label className="av-field">
                <span>{t('payments.fields.invoice')}</span>
                <SelectField
                  options={[
                    { value: '', label: t('common.notSet') },
                    ...draftCustomerInvoices.map((invoice) => ({ value: invoice.id, label: invoice.label }))
                  ]}
                  value={draft.invoiceId}
                  onChange={(event) => setDraft((current) => ({ ...current, invoiceId: event.target.value }))}
                />
              </label>
              <label className="av-field av-field--span-3">
                <span>{t('payments.fields.projectDescription')}</span>
                <TextAreaField
                  rows={3}
                  value={draft.projectDescription}
                  onValueChange={(projectDescription) => setDraft((current) => ({ ...current, projectDescription }))}
                />
              </label>
              <label className="av-field av-field--span-3">
                <span>{t('payments.fields.paymentTerms')}</span>
                <TextAreaField
                  rows={2}
                  value={draft.paymentTermsText}
                  onValueChange={(paymentTermsText) => setDraft((current) => ({ ...current, paymentTermsText }))}
                />
              </label>
              <label className="av-field">
                <span>{t('payments.fields.deliveryWorkingDays')}</span>
                <TextField
                  inputMode="numeric"
                  value={draft.deliveryWorkingDays}
                  onValueChange={(deliveryWorkingDays) => setDraft((current) => ({ ...current, deliveryWorkingDays }))}
                />
              </label>
              <label className="av-field">
                <span>{t('payments.fields.revisionRounds')}</span>
                <TextField
                  inputMode="numeric"
                  value={draft.revisionRounds}
                  onValueChange={(revisionRounds) => setDraft((current) => ({ ...current, revisionRounds }))}
                />
              </label>
              <label className="av-field">
                <span>{t('payments.fields.acceptanceWorkingDays')}</span>
                <TextField
                  inputMode="numeric"
                  value={draft.acceptanceWorkingDays}
                  onValueChange={(acceptanceWorkingDays) => setDraft((current) => ({ ...current, acceptanceWorkingDays }))}
                />
              </label>
              <label className="av-field">
                <span>{t('payments.fields.warrantyDays')}</span>
                <TextField
                  inputMode="numeric"
                  value={draft.warrantyDays}
                  onValueChange={(warrantyDays) => setDraft((current) => ({ ...current, warrantyDays }))}
                />
              </label>
              <label className="av-field av-field--span-2">
                <span>{t('payments.fields.serviceSummary')}</span>
                <TextAreaField
                  rows={2}
                  value={draft.serviceSummary}
                  onValueChange={(serviceSummary) => setDraft((current) => ({ ...current, serviceSummary }))}
                />
              </label>
            </div>
          </section>

          <section className="av-payment-create-step">
            <header className="av-payment-create-step__header">
              <h3>{t('payments.create.sections.preview')}</h3>
              <p>{t('payments.create.previewNote')}</p>
            </header>
            <div className="av-payment-contract-preview av-payment-contract-preview--plain">
              <pre className="av-payment-agreement-preview-text">{agreementPreviewText}</pre>
            </div>
          </section>
        </div>
        <div className="av-payment-create-actions">
          {mutationError ? <p className="av-form-error">{mutationError}</p> : null}
          <button className="av-primary-button" disabled={createDisabled} type="button" onClick={() => void handleCreatePayment()}>
            <Icon name="payments" />
            <span>{isSaving ? t('payments.actions.creating') : t('payments.actions.create')}</span>
          </button>
        </div>
      </PanelCard>

      <PanelCard title={t('payments.list.title')}>
        <div className="av-toolbar av-payment-toolbar">
          <SelectField
            options={[{ value: 'all', label: t('payments.filters.allStatuses') }, ...paymentStatuses.map((status) => ({ value: status, label: t(`enums.paymentStatus.${status}`) }))]}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as 'all' | PaymentOrderStatus)}
          />
          <SelectField value={customerFilter} options={customerOptions} onChange={(event) => setCustomerFilter(event.target.value)} />
          <SelectField
            options={[{ value: 'all', label: t('payments.filters.allCurrencies') }, ...(['RON', 'EUR', 'USD'] satisfies PaymentCurrency[]).map((currency) => ({ value: currency, label: currency }))]}
            value={currencyFilter}
            onChange={(event) => setCurrencyFilter(event.target.value as 'all' | PaymentCurrency)}
          />
          <SelectField
            options={[
              { value: 'updatedAt', label: t('payments.sort.updatedAt') },
              { value: 'amount', label: t('payments.sort.amount') },
              { value: 'status', label: t('payments.sort.status') }
            ]}
            value={sort}
            onChange={(event) => setSort(event.target.value as typeof sort)}
          />
          <button
            className="av-secondary-button"
            type="button"
            onClick={() => setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))}
          >
            <Icon name={sortDirection === 'asc' ? 'arrowUp' : 'arrowDown'} />
            <span>{sortDirection === 'asc' ? t('payments.sort.asc') : t('payments.sort.desc')}</span>
          </button>
        </div>

        {rows.length === 0 ? (
          <EmptyCollectionState title={t('payments.empty.title')} description={t('payments.empty.description')} />
        ) : (
          <div className="av-table-wrap">
            <table className="av-collection-table">
              <thead>
                <tr>
                  <th>{t('payments.table.title')}</th>
                  <th>{t('payments.table.customer')}</th>
                  <th>{t('payments.table.status')}</th>
                  <th>{t('payments.table.amount')}</th>
                  <th>{t('payments.table.sync')}</th>
                  <th>{t('payments.table.updated')}</th>
                  <th aria-label={t('payments.table.actions')} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link className="av-table-link" to={`/payments/${row.id}`}>
                        {row.title}
                      </Link>
                      <span className={['av-muted-line', row.isPublicLinkReady ? '' : 'is-warning'].filter(Boolean).join(' ')}>
                        {row.isPublicLinkReady ? row.publicUrl : t('payments.sync.localOnlyWarning')}
                      </span>
                      {row.syncError ? <span className="av-muted-line is-warning">{row.syncError}</span> : null}
                    </td>
                    <td>{row.customerName}</td>
                    <td>
                      <StatusBadge label={row.statusLabel} tone={row.statusTone} />
                    </td>
                    <td>{row.amountLabel}</td>
                    <td>{row.syncLabel}</td>
                    <td>{row.updatedLabel}</td>
                    <td className="av-table-actions">
                      <button className="av-secondary-button" type="button" onClick={() => void handleSync(row.id)}>
                        <Icon name="sync" />
                        <span>{t('payments.actions.sync')}</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PanelCard>
    </div>
  );
}
